import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { PlusCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '../lib/api';
import { getCurrencySymbol } from '../lib/currencies';

const walkInAPI = { create: (d) => API.post('/bookings/walk-in', d) };
const roomsAPI = { list: (pid) => API.get(`/rooms?property_id=${pid}`) };
const guestsAPI = { search: (pid, q) => API.get(`/guests/search?property_id=${pid}&q=${q}`) };

function todayISO() { return new Date().toISOString().split('T')[0]; }
function daysBetween(a, b) { return Math.max(1, Math.round((new Date(b+'T00:00:00') - new Date(a+'T00:00:00'))/86400000)); }

export function NewBookingModal({ open, onOpenChange, properties, selectedProperty, onCreated }) {
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [guestQuery, setGuestQuery] = useState('');
  const [guestResults, setGuestResults] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestSearching, setGuestSearching] = useState(false);
  const guestSearchTimeout = useRef(null);

  const initForm = useCallback(() => {
    const prop = properties?.find(p => p.id === selectedProperty);
    setForm({
      property_id: selectedProperty || '', room_type_id: '', check_in: todayISO(), check_out: '',
      guest_name: '', guest_email: '', guest_phone: '', guest_country: '', guest_id: '',
      total_amount: 0, currency: prop?.currency || 'USD', notes: '',
    });
    setGuestQuery(''); setGuestResults([]); setSelectedGuest(null);
  }, [properties, selectedProperty]);

  useEffect(() => { if (open) initForm(); }, [open, initForm]);

  useEffect(() => {
    if (selectedProperty) roomsAPI.list(selectedProperty).then(r => setRooms(r.data)).catch(() => {});
  }, [selectedProperty]);

  useEffect(() => {
    if (form.check_in && form.check_out && form.room_type_id) {
      const nights = Math.max(1, daysBetween(form.check_in, form.check_out));
      const room = rooms.find(r => r.id === form.room_type_id);
      if (room) {
        let rate = room.price_per_night;
        if (form.currency !== room.currency && room.currency_rates?.[form.currency]) rate = room.currency_rates[form.currency];
        setForm(prev => ({ ...prev, total_amount: rate * nights }));
      }
    }
  }, [form.check_in, form.check_out, form.room_type_id, rooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.check_out) { toast.error('Please select check-out date'); return; }
    setSaving(true);
    try {
      const res = await walkInAPI.create(form);
      toast.success('Booking created!', { description: `Ref: ${res.data.booking_reference}` });
      onOpenChange(false);
      onCreated?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create booking'); }
    finally { setSaving(false); }
  };

  const handleGuestSearch = (q) => {
    setGuestQuery(q); setForm(prev => ({ ...prev, guest_name: q, guest_id: '' })); setSelectedGuest(null);
    clearTimeout(guestSearchTimeout.current);
    if (q.length < 2) { setGuestResults([]); return; }
    guestSearchTimeout.current = setTimeout(async () => {
      setGuestSearching(true);
      try { const res = await guestsAPI.search(form.property_id, q); setGuestResults(res.data.guests || []); } catch { setGuestResults([]); } finally { setGuestSearching(false); }
    }, 300);
  };

  const selectGuest = (guest) => {
    setSelectedGuest(guest); setGuestQuery(guest.name); setGuestResults([]);
    setForm(prev => ({ ...prev, guest_name: guest.name, guest_email: guest.email || '', guest_phone: guest.phone || '', guest_country: guest.country || '', guest_id: guest.id }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PlusCircle className="h-5 w-5" /> New Booking</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Property</Label>
              <Select value={form.property_id} onValueChange={v => { setForm(prev => ({ ...prev, property_id: v, room_type_id: '' })); roomsAPI.list(v).then(r => setRooms(r.data)); }}>
                <SelectTrigger data-testid="walkin-property"><SelectValue placeholder="Property" /></SelectTrigger>
                <SelectContent>{(properties || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Room Type *</Label>
              <Select value={form.room_type_id} onValueChange={v => { const room = rooms.find(r => r.id === v); setForm(prev => ({ ...prev, room_type_id: v, currency: room?.currency || prev.currency || 'USD', total_amount: room?.price_per_night || 0 })); }}>
                <SelectTrigger data-testid="walkin-room"><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>{rooms.filter(r => r.property_id === form.property_id).map(r => <SelectItem key={r.id} value={r.id}>{r.name} — {getCurrencySymbol(r.currency || 'USD')}{r.price_per_night}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Check-in *</Label><Input type="date" value={form.check_in} onChange={e => setForm(prev => ({ ...prev, check_in: e.target.value }))} required data-testid="walkin-checkin" /></div>
            <div className="space-y-1.5"><Label>Check-out *</Label><Input type="date" value={form.check_out} min={form.check_in} onChange={e => setForm(prev => ({ ...prev, check_out: e.target.value }))} required data-testid="walkin-checkout" /></div>
          </div>
          <div className="space-y-1.5 relative">
            <Label>Guest Name *</Label>
            <Input value={guestQuery} onChange={e => handleGuestSearch(e.target.value)} placeholder="Search guests..." required autoComplete="off" data-testid="walkin-name" />
            {selectedGuest && <p className="text-xs text-blue-600 mt-0.5">Linked: {selectedGuest.name}</p>}
            {guestResults.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-md bg-white shadow-lg max-h-40 overflow-y-auto" data-testid="guest-suggestions">
                {guestResults.map(g => <button key={g.id} type="button" className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b last:border-0" onClick={() => selectGuest(g)}><span className="font-medium">{g.name}</span><span className="text-gray-400 ml-2">{g.email}</span></button>)}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.guest_email} onChange={e => setForm(prev => ({ ...prev, guest_email: e.target.value }))} data-testid="walkin-email" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.guest_phone} onChange={e => setForm(prev => ({ ...prev, guest_phone: e.target.value }))} data-testid="walkin-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Currency *</Label>
              <Select value={form.currency} onValueChange={code => {
                const room = rooms.find(r => r.id === form.room_type_id);
                const nights = form.check_in && form.check_out ? Math.max(1, daysBetween(form.check_in, form.check_out)) : 1;
                let rate = room?.price_per_night || 0;
                if (room?.currency_rates?.[code]) rate = room.currency_rates[code];
                setForm(prev => ({ ...prev, currency: code, total_amount: rate * nights }));
              }}>
                <SelectTrigger data-testid="walkin-currency"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const room = rooms.find(r => r.id === form.room_type_id);
                    const rates = [];
                    if (room) { rates.push({ code: room.currency || 'USD', rate: room.price_per_night, def: true }); if (room.currency_rates) Object.entries(room.currency_rates).forEach(([c, r]) => { if (c !== room.currency) rates.push({ code: c, rate: r }); }); }
                    return rates.length > 0 ? rates.map(r => <SelectItem key={r.code} value={r.code}>{getCurrencySymbol(r.code)} {r.code} — {r.rate}/night{r.def ? ' (default)' : ''}</SelectItem>) : <SelectItem value={form.currency || 'USD'}>{form.currency || 'USD'}</SelectItem>;
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Total Amount *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{getCurrencySymbol(form.currency || 'USD')}</span>
                <Input type="number" min="0" step="0.01" value={form.total_amount} onChange={e => setForm(prev => ({ ...prev, total_amount: parseFloat(e.target.value) || 0 }))} className="pl-8" required data-testid="walkin-amount" />
              </div>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Special requests..." rows={2} data-testid="walkin-notes" /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="walkin-submit">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create Booking</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
